import React, { useMemo } from 'react';

import type { Apollo } from 'src/gql/generated';
import type { ReactSetStateAction } from 'src/types';
import type { WorkflowStep } from 'src/workflow';
import { EmptyWorkflowSection, NewStep, StepTimelineEntry, useTimelineStyles } from 'src/workflow';

interface StepsListProps {
  activeStep: WorkflowStep | null;
  emptySectionContent: string;
  newStepActive: boolean;
  orderedSteps: WorkflowStep[];
  setNewStepActive: ReactSetStateAction<boolean>;
  workflowId: UUID;
  workflowState: Apollo.WorkflowState;
  showProgress?: boolean;
}

export function StepsList({
  activeStep,
  emptySectionContent,
  newStepActive,
  orderedSteps,
  setNewStepActive,
  showProgress = true,
  workflowId,
  workflowState,
}: StepsListProps): JSX.Element {
  const { classes: timelineClasses } = useTimelineStyles();
  const sectionIsEmpty = useMemo(
    () => orderedSteps.every(step => !step.actions.length),
    [orderedSteps]
  );

  if (sectionIsEmpty && !newStepActive)
    return (
      <EmptyWorkflowSection
        content={emptySectionContent}
        showProgress={showProgress}
        workflowId={workflowId}
      />
    );

  return (
    <div className={timelineClasses.timeline}>
      {orderedSteps.map((step, index) => {
        return (
          <StepTimelineEntry
            key={index}
            activeStep={activeStep}
            showProgress={showProgress}
            step={step}
            stepNumber={index + 1}
            totalNumSteps={orderedSteps.length}
            workflowId={workflowId}
            workflowState={workflowState}
          />
        );
      })}
      {newStepActive && (
        <NewStep
          sectionSteps={orderedSteps}
          setNewStepActive={setNewStepActive}
          showProgress={showProgress}
          workflowId={workflowId}
        />
      )}
    </div>
  );
}
